// src/components/captain/CaptainHindsight.jsx
import React, { useState } from 'react';
import { Award } from 'lucide-react';
import { useManagerContext } from '../../context/DataContext';
import { useDelayedEffect } from '../../hooks/useDelayedEffect';
import { 
  PageContainer, 
  Card, 
  NavigationButtons,
  ProgressBar
} from '../shared/Navigation';

const CaptainHindsight = () => {
  const [showDetails, setShowDetails] = useState(false);
  
  const { getManagerData } = useManagerContext();
  const managerData = getManagerData();
  
  // Extract captain data
  const { summary: captainSummary, details: captainDetails = [] } = 
    managerData?.captainHindsight || {};
  
  // Use delayed effect for animations
  const { currentStep } = useDelayedEffect([500, 400, 400]);
  
  // Navigation functions  
  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };
  
  // Calculate missed gameweeks (assuming average of 5 points per gameweek)
  const missedGameweeks = captainSummary 
    ? Math.round(captainSummary.Points_Missed / 5)
    : 0;
  
  // Custom button for navigation
  const DetailsButton = (
    <button 
      onClick={toggleDetails}
      className="flex items-center justify-center px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full hover:bg-indigo-200 transition-colors"
    >
      <Award size={18} className="mr-2" />
      {showDetails ? 'Hide Details' : 'See Worst Decisions'}
    </button>
  );
  
  // If we don't have captain data, show a placeholder
  if (!captainSummary) {
    return (
      <PageContainer title="Captain Choices">
        <Card className="text-center">
          <p>No captain data available for this manager.</p>
          
          <NavigationButtons 
            backPath="/journey-selector"
            forwardPath="/my-journey"
          />
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer 
      title="Captain Choices"
      titleColor="text-purple-300"
      gradientFrom="from-purple-900"
      gradientTo="to-indigo-900"
    >
      <Card>
        <p className="text-center text-gray-600 font-medium mb-6">Actual vs. Optimal</p>
        
        <div className="flex justify-between items-center mb-8">
          <div className="text-center">
            <div 
              className={`text-5xl font-bold text-purple-400 mb-1 opacity-0 ${currentStep >= 0 ? 'animate-fade-in' : ''}`}
              style={{ animationDelay: '0.2s' }}
            >
              {captainSummary.Actual_Captain_Points}
            </div>
            <div className="text-gray-500 text-sm">ACTUAL</div>
          </div>
          
          <div className="h-16 w-px bg-gray-200"></div>
          
          <div className="text-center">
            <div 
              className={`text-5xl font-bold text-purple-600 mb-1 opacity-0 ${currentStep >= 1 ? 'animate-fade-in' : ''}`}
              style={{ animationDelay: '0.6s' }}
            >
              {captainSummary.Optimal_Captain_Points}
            </div>
            <div className="text-gray-500 text-sm">OPTIMAL</div>
          </div>
        </div>
        
        <ProgressBar 
          value={captainSummary.Actual_Captain_Points} 
          max={captainSummary.Optimal_Captain_Points}
          animate={true}
          className="mb-8"
        />
        
        <div 
          className={`bg-purple-50 p-4 rounded-lg opacity-0 ${currentStep >= 2 ? 'animate-fade-in' : ''}`}
          style={{ animationDelay: '1s' }}
        >
          <p className="text-gray-700">
            <span className="font-bold">Worst decision:</span> GW{captainSummary.Worst_GW} when you chose {captainSummary.Worst_GW_Actual_Captain} instead of {captainSummary.Worst_GW_Optimal_Captain}, missing {captainSummary.Worst_GW_Points_Diff} points!
          </p>
        </div>
        
        {showDetails && captainDetails.length > 0 && (
          <div className="mt-6 max-h-40 overflow-y-auto">
            <h3 className="font-bold text-purple-600 mb-2">Your Top 5 Captain Misses</h3>
            <ul className="space-y-2">
              {captainDetails
                .sort((a, b) => b.points_diff - a.points_diff)
                .slice(0, 5)
                .map((detail, index) => (
                  <li key={index} className="text-sm bg-gray-50 p-2 rounded">
                    <div className="font-semibold">GW{detail.gw}: {detail.actual_captain} → {detail.optimal_captain}</div>
                    <div className="text-gray-500">Missed {detail.points_diff} points</div>
                  </li>
                ))}
            </ul>
          </div>
        )}
      </Card>
      
      <div className="text-center mt-6 w-full max-w-md">
        <p className="text-lg font-semibold">
          You missed {captainSummary.Points_Missed} potential captain points!
        </p>
        <p className="text-gray-300 mb-4">
          That's like throwing away {missedGameweeks} gameweeks.
        </p>
        
        <NavigationButtons 
          backPath="/journey-selector"
          forwardPath="/my-journey"
          backText="Back to Journey Selector"
          forwardText="Go to My Journey"
          centerButton={DetailsButton}
        />
      </div>
    </PageContainer>
  );
};

export default CaptainHindsight;
